import React, { useEffect, useRef } from 'react';

export default function SnowEffect() {
  const containerRef = useRef<HTMLDivElement>(null);
  const snowflakesRef = useRef<HTMLDivElement[]>([]);
  const frameRef = useRef<number>();
  const isRunningRef = useRef(true);
  const isVisibleRef = useRef(true);
  const lastTimeRef = useRef(0);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const maxSnowflakes = isMobile ? 10 : 20;
    const interval = isMobile ? 400 : 300;
    const FPS = isMobile ? 24 : 30;
    const frameTime = 1000 / FPS;

    const createSnowflake = (timestamp: number) => {
      if (!containerRef.current || !isRunningRef.current || !isVisibleRef.current) return;
      if (snowflakesRef.current.length >= maxSnowflakes) return;
      if (timestamp - lastTimeRef.current < frameTime) return;

      lastTimeRef.current = timestamp;

      const snowflake = document.createElement('div');
      snowflake.classList.add('snowflake');
      
      const size = isMobile ? Math.random() * 3 + 2 : Math.random() * 4 + 3;
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      const duration = Math.random() * 2 + (isMobile ? 3 : 4);
      
      snowflake.style.cssText = `
        --start-x: ${startX}px;
        --end-x: ${endX}px;
        width: ${size}px;
        height: ${size}px;
        opacity: ${Math.random() * 0.4 + 0.2};
        animation: snowfall ${duration}s linear forwards;
        transform: translate3d(0, 0, 0);
        will-change: transform;
      `;

      containerRef.current.appendChild(snowflake);
      snowflakesRef.current.push(snowflake);

      snowflake.addEventListener('animationend', () => {
        snowflake.remove();
        snowflakesRef.current = snowflakesRef.current.filter(s => s !== snowflake);
      }, { once: true });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        isVisibleRef.current = entries[0].isIntersecting;
        if (!entries[0].isIntersecting && frameRef.current) {
          cancelAnimationFrame(frameRef.current);
        } else if (entries[0].isIntersecting) {
          animate();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    const animate = () => {
      if (!isRunningRef.current || !isVisibleRef.current) return;
      
      frameRef.current = requestAnimationFrame((timestamp) => {
        createSnowflake(timestamp);
        setTimeout(animate, interval);
      });
    };

    animate();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        isRunningRef.current = false;
        if (frameRef.current) {
          cancelAnimationFrame(frameRef.current);
        }
      } else {
        isRunningRef.current = true;
        animate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isRunningRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observer.disconnect();
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      snowflakesRef.current.forEach(snowflake => snowflake.remove());
      snowflakesRef.current = [];
    };
  }, []);

  return <div ref={containerRef} className="snow-container" />;
}